import React, { FC, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Button, Card, Dropdown, Empty, Menu, Modal } from 'antd';
import { EditOutlined, EnvironmentOutlined, MoreOutlined, ToolOutlined } from '@ant-design/icons';
import PaginatedReduxTable from '../paginated_redux_table/PaginatedReduxTable';
import {
	complianceRequestsRestCrudThunks,
	fetchComplianceRequestsWithInstanceCounts,
	publishComplianceRequest,
} from '../../thunks/compliance_requests_thunks';
import { fetchComplianceSummary } from '../../thunks/compliance_request_instances_thunks';
import { fetchComplianceSupplierStatuses } from '../../thunks/compliance_statuses_thunks';
import { withUserType } from '../withUserType';
import { COMPLIANCE_REQUEST_LIFECYCLES, ComplianceRequest } from '../../types/ComplianceTypes';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import NewComplianceRequestModal from './NewComplianceRequestModal';
import ComplianceRequestDetailModal from './ComplianceRequestDetailModal';
import ApprovalStatusCounts from './ApprovalStatusCounts';
import { RequestedComplianceCell } from '../compliance_common/RequestedComplianceCell';

const REQUESTS_INDEX_TARGET_COLLECTION = 'buyerComplianceRequestsIndex';
const REQUESTS_COLLECTION = 'compliance_requests';
const INITIAL_PAGINATION = { current: 1, pageSize: 10 };
// First draft: country is fixed to US (no UI filter), sent as a base param. Stable
// reference so PaginatedReduxTable doesn't refetch on every render.
const US_COUNTRY_PARAMS = { countryCode: 'US' };

const formatDate = (isoDate) => (isoDate ? moment(isoDate).format('D MMM, YYYY') : '--');

interface BuyerComplianceRequestsPageProps {
	// Which compliance endpoints to hit. Falls back to the logged-in user's role
	// (session.userType, injected by withUserType) when not explicitly provided.
	roleType?: string;
	refetchComplianceRequests: () => void;
	// Refetch derived compliance (shell banner + Overview supplier badges) after a
	// delete re-derives it. Publish already does this via its thunk.
	refreshComplianceState: () => void;
	publishRequest: (id: number) => Promise<any>;
	deleteComplianceRequest: (request: ComplianceRequest) => Promise<any>;
}

const BuyerComplianceRequestsPage: FC<BuyerComplianceRequestsPageProps> = ({
	roleType,
	userType,
	refetchComplianceRequests,
	refreshComplianceState,
	publishRequest,
	deleteComplianceRequest,
}): React.ReactElement => {
	const role = roleType || userType;
	const [editingRequest, setEditingRequest] = useState(null);
	const [detailRequest, setDetailRequest] = useState(null);

	// PaginatedReduxTable dispatches its fetchData prop itself, so it must be the
	// raw thunk-returning read method (not a pre-dispatched wrapper). The requests
	// tab uses the hydrated with_instance_counts read so each row has
	// instanceStatusCounts + targetedSupplierCount. Memoized on role so the table
	// doesn't refetch on every render.
	const fetchComplianceRequests = useMemo(
		() => fetchComplianceRequestsWithInstanceCounts(role),
		[role]
	);

	const closeDetailModal = () => setDetailRequest(null);
	const closeEditModal = () => setEditingRequest(null);

	const handleEditSuccess = () => {
		setEditingRequest(null);
		refetchComplianceRequests();
	};

	const handlePublish = (request) => {
		publishRequest(request.id).then(refetchComplianceRequests);
	};

	const handleDelete = (request) => {
		deleteComplianceRequest(request).then(() => {
			refetchComplianceRequests();
			// Deleting a published request removes its instances and can re-derive
			// supplier compliance, so refresh the shell banner + Overview badges.
			refreshComplianceState();
		});
	};

	const confirmDelete = (request) => {
		Modal.confirm({
			title: `Delete request "${request.name}"?`,
			content: 'Suppliers will no longer see this compliance request.',
			okText: 'Delete',
			okType: 'danger',
			onOk: () => handleDelete(request),
		});
	};

	// Only a published request has instances to show, so only those open the detail
	// modal; drafts render the title as plain text.
	const renderRequestedCompliance = (request: ComplianceRequest) => {
		const openDetail = () => setDetailRequest(request);
		return (
			<RequestedComplianceCell
				complianceRequest={request}
				onTitleClick={
					request.status === COMPLIANCE_REQUEST_LIFECYCLES.PUBLISHED ? openDetail : undefined
				}
			/>
		);
	};

	const renderRequestDetails = (request: ComplianceRequest) => {
		const locationLabel = request.allLocations
			? 'All'
			: `${nullSafeGetOrElse('locationIds', request, []).length} locations`;
		const supplierCount = nullSafeGetOrElse('targetedSupplierCount', request, 0);
		return (
			<div className="flex flex-col gap-1">
				<div className="bodymedium">
					{request.isMandatory ? 'Mandatory' : 'Optional'}
					{request.requiresUniqueDocument ? ' • Unique doc' : ''}
				</div>
				<div className="footnoteregular flex flex-row flex-wrap items-center gap-2 text-character-secondary">
					<span>
						<EnvironmentOutlined className="mr-1" />
						{locationLabel}
					</span>
					<span>
						<ToolOutlined className="mr-1" />
						{request.allSuppliers
							? `All (${supplierCount} Suppliers)`
							: `${supplierCount} Suppliers`}
					</span>
				</div>
				{request.notes ? (
					<div className="footnoteregular border-0 border-l-2 border-solid border-grey-4 bg-grey-2 py-1 pl-2 text-character-secondary">
						{request.notes}
					</div>
				) : null}
			</div>
		);
	};

	const renderApprovalStatus = (request: ComplianceRequest) => {
		if (request.status === COMPLIANCE_REQUEST_LIFECYCLES.DRAFT) {
			const publishRequestRow = () => handlePublish(request);
			return (
				<div className="flex flex-col gap-1">
					<div className="bodyregular flex flex-row items-center gap-2 whitespace-nowrap">
						<EditOutlined />
						<span>Draft</span>
						<span className="inline-block h-4 w-px bg-grey-5" />
						<Button type="link" className="p-0" onClick={publishRequestRow}>
							Publish
						</Button>
						<span className="text-grey-8">to send request.</span>
					</div>
					{request.isMandatory && request.dueBy ? (
						<div className="footnoteregular text-grey-8">
							Suppliers must submit and receive your approval by {formatDate(request.dueBy)} to
							remain compliant.
						</div>
					) : null}
				</div>
			);
		}
		return <ApprovalStatusCounts complianceRequest={request} roleType={role} />;
	};

	const renderRowMenu = (request: ComplianceRequest) => {
		const isDraft = request.status === COMPLIANCE_REQUEST_LIFECYCLES.DRAFT;
		const handleEditClick = () => setEditingRequest(request);
		const handlePublishClick = () => handlePublish(request);
		const handleDeleteClick = () => confirmDelete(request);
		return (
			<Menu>
				{/* Requests are editable in both Draft and Published states (backend
				    add-only reconcile), so Edit is always available; Publish stays
				    Draft-only. */}
				<Menu.Item key="edit" onClick={handleEditClick}>
					Edit Request
				</Menu.Item>
				{isDraft ? (
					<Menu.Item key="publish" onClick={handlePublishClick}>
						Publish Request
					</Menu.Item>
				) : null}
				{request.sampleFileUrl ? (
					<Menu.Item key="sample">
						<a href={request.sampleFileUrl} target="_blank" rel="noopener noreferrer">
							View Sample
						</a>
					</Menu.Item>
				) : null}
				<Menu.Item key="delete" danger={true} onClick={handleDeleteClick}>
					Delete Request
				</Menu.Item>
			</Menu>
		);
	};

	// Request-level "expiration" = the compliance due date. Per-document expiry
	// rollups (Up to date / Expiring soon) aren't provided by the backend for a
	// request, so we show the due date; '--' for optional requests with no date.
	const renderExpiration = (request: ComplianceRequest) =>
		request.dueBy ? (
			<span className="bodyregular">{formatDate(request.dueBy)}</span>
		) : (
			<span className="text-character-secondary">--</span>
		);

	const tableColumns = [
		{
			title: 'Requested Compliance',
			key: 'name',
			width: '24%',
			render: (text, request) => renderRequestedCompliance(request),
		},
		{
			title: 'Request Details',
			key: 'details',
			width: '20%',
			render: (text, request) => renderRequestDetails(request),
		},
		{
			title: 'Approval Status',
			key: 'approvalStatus',
			width: '30%',
			render: (text, request) => renderApprovalStatus(request),
		},
		{
			title: 'Expiration Date',
			key: 'expiration',
			width: '14%',
			render: (text, request) => renderExpiration(request),
		},
		{
			title: '',
			key: 'actions',
			width: 40,
			align: 'center' as const,
			// Hug the kebab button: no cell padding so the column is ~button width.
			onCell: () => ({ style: { paddingLeft: 0, paddingRight: 0 } }),
			onHeaderCell: () => ({ style: { paddingLeft: 0, paddingRight: 0 } }),
			render: (text, request) => (
				<Dropdown overlay={renderRowMenu(request)} trigger={['click']}>
					<Button icon={<MoreOutlined />} />
				</Dropdown>
			),
		},
	];

	const emptyState = (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description="Compliance requests you create will appear here."
		/>
	);

	return (
		<div className=" pb-6">
			<Card bordered={false} bodyStyle={{ padding: 8 }}>
				<PaginatedReduxTable
					mode="list"
					// Sync filters (incl. search) to the URL so AdvancedFilters' search box
					// clears correctly (it derives initialSearch from the URL each render).
					// Mirrors the work-order index tables.
					updateQueryParams={true}
					showHeader={true}
					collectionName={REQUESTS_COLLECTION}
					targetCollectionName={REQUESTS_INDEX_TARGET_COLLECTION}
					columns={tableColumns}
					keyAccessor={(request) => request.id}
					initialPagination={INITIAL_PAGINATION}
					additionalParams={US_COUNTRY_PARAMS}
					fetchData={fetchComplianceRequests}
					emptyState={emptyState}
				/>
			</Card>
			{editingRequest && (
				<NewComplianceRequestModal
					visible={!!editingRequest}
					roleType={role}
					editingRequest={editingRequest}
					onCancel={closeEditModal}
					onSuccess={handleEditSuccess}
				/>
			)}
			{detailRequest && (
				<ComplianceRequestDetailModal
					visible={!!detailRequest}
					roleType={role}
					complianceRequest={detailRequest}
					onCancel={closeDetailModal}
				/>
			)}
		</div>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => {
	const role = ownProps.roleType || ownProps.userType;
	return {
		// Replay the collection's current filters + pagination (read at call time via
		// getState) so an edit/publish/delete keeps the user on the same filtered page
		// instead of snapping back to unfiltered page 1 under still-active filter pills.
		refetchComplianceRequests: () =>
			dispatch((innerDispatch, getState) => {
				const slice = getState().compliance_requests;
				const filters = nullSafeGetOrElse(`${REQUESTS_INDEX_TARGET_COLLECTION}.filters`, slice, {});
				const pagination = nullSafeGetOrElse(
					`${REQUESTS_INDEX_TARGET_COLLECTION}.pagination`,
					slice,
					INITIAL_PAGINATION
				);
				return innerDispatch(
					fetchComplianceRequestsWithInstanceCounts(role)(
						{ ...US_COUNTRY_PARAMS },
						REQUESTS_INDEX_TARGET_COLLECTION,
						pagination,
						null,
						filters
					)
				);
			}),
		// Both redux-backed: the shell banner reads the summary, the Overview badges
		// read the supplier statuses, so refetching them re-renders both.
		refreshComplianceState: () => {
			dispatch(fetchComplianceSummary(role)());
			dispatch(fetchComplianceSupplierStatuses(role)());
		},
		publishRequest: (id) => dispatch(publishComplianceRequest(role)(id)),
		deleteComplianceRequest: (request) =>
			dispatch(complianceRequestsRestCrudThunks(role).deleteImmediate(request, 'id')),
	};
};

export default withUserType(connect(null, mapDispatchToProps)(BuyerComplianceRequestsPage));
